import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import { defaultPensionCalCulationMethodOptions } from '@nxsys/constants/AppConst';
import {
  defaultAEPostponementDaysOptions,
  defaultPensionMethodOptions,
  pensionProviderIdOptions,
} from '@nxsys/constants/AppConstants';
import {
  getPensionSelfCertMins,
  isNowLikePensionProvider,
  PENSION_UNCERTIFIED_MINS,
} from '@nxsys/constants/AppEnums';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import SelfCertificationPanel from '../../detail/components/SelfCertificationPanel';
import {
  useConnectNestPensionDraft,
  useConnectSmartPensionDraft,
  useCreateCompanyDraft,
  useGetCompanyDraftPensionStatus,
} from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row } from 'antd';
import AppRateInput from '@nxsys/components/AppRateInput';
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

// Types
export type PensionContinueValues = {
  pensionProviderId: Option<string> | null;
  aePostponementDays: Option<string> | null;
  erPensionPercentage: number;
  eePensionPercentage: number;
  pensionSelfCertification: string;
  pensionCalculationType: Option<string> | null;
  pensionMethod: Option<string> | null;
  isSalarySacrifice: boolean;
  // Smart Pension fields
  companyId: string;
  pensionSchemeId: string;
  pensionSchemeNumber: string;
  // NOW Pension fields
  employerCode: string;
  payrollCode: string;
  schemeCodeWeekly: string;
  schemeCode2Weekly: string;
  schemeCode4Weekly: string;
  schemeCodeMonthly: string;
  // Jarvis Pension fields
  jarvisGroupOrganizationApiKey: string;
  jarvisOrganizationID: string;
  jarvisPensionScheme: string;
  // Nest Pension credential fields
  nestUsername: string;
  nestPassword: string;
  nestEmployerId: string;
  nestDelegateId: string;

  // use for connect pension
  companyDraftId: number;
};

export type PensionContinueFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => PensionContinueValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<PensionContinueFormRef>;
  initialValues: Partial<PensionContinueValues>;
  stepNumber: number;
};

const providerOptions = pensionProviderIdOptions;

const PensionContinue: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<PensionContinueValues>();
  const selectedProvider = Form.useWatch('pensionProviderId', form);

  const isSmartPension = selectedProvider?.value === 'SMART_PENSION';
  const isNowPension = isNowLikePensionProvider(selectedProvider?.value ?? '');
  const isNestPension = selectedProvider?.value === 'NEST_PENSION';
  // NX-4803: Jarvis pension integration hidden until complete
  // const isJarvisPension =
  //   selectedProvider?.value === PensionProvidersNameEnum.JARVIS_PENSION;
  const isSmartOrNow = isSmartPension || isNowPension;

  const { run: createCompanyDraftRun } = useCreateCompanyDraft();

  const createCompanyDraft = useCallback(async () => {
    const companyDraft = await createCompanyDraftRun({
      frontendData: {},
    });
    form.setFieldValue('companyDraftId', companyDraft.id);
    return companyDraft;
  }, [createCompanyDraftRun, form]);

  // Smart Pension connect
  const { run: connectSmartPensionDraft, loading: isConnectingSmartPension } =
    useConnectSmartPensionDraft();
  const {
    status: pensionStatus,
    polling: isPollingPension,
    error: pensionPollingError,
    startPolling,
  } = useGetCompanyDraftPensionStatus();

  const handleConnectSmartPension = useCallback(async () => {
    const companyDraft = await createCompanyDraft();
    const draftId = companyDraft.id;
    const res = await connectSmartPensionDraft(draftId, {});
    if (res.oauthUrl) {
      window.open(res.oauthUrl, '_blank');
      startPolling(draftId);
    }
  }, []);

  useEffect(() => {
    if (pensionStatus?.smartPension.status === 'AUTHORIZED') {
      const { schemeData, smartPensionCompanyId } = pensionStatus.smartPension;
      form.setFieldsValue({
        companyId: smartPensionCompanyId?.toString(),
        pensionSchemeId: schemeData?.pensionSchemeId?.toString(),
        pensionSchemeNumber: schemeData?.pensionSchemeName,
        erPensionPercentage: schemeData?.erPensionPercentage,
        aePostponementDays: schemeData?.aePostponementDays
          ? defaultAEPostponementDaysOptions.find(
              (o) => o.label === schemeData.aePostponementDays,
            )
          : undefined,
      } as Partial<PensionContinueValues>);
      showMessage({
        type: 'success',
        content: 'Successfully connected to Smart Pension!',
      });
    }
    if (pensionStatus?.smartPension.status === 'ERROR') {
      showMessage({
        type: 'error',
        content:
          pensionStatus.smartPension.errorMessage ||
          'Smart Pension connection failed',
      });
    }
  }, [pensionStatus?.smartPension.status]);

  // Nest Pension connect
  const { run: connectNestPensionDraft } = useConnectNestPensionDraft();

  const [selfCertExpanded, setSelfCertExpanded] = useState(false);
  const [selfCertValue, setSelfCertValue] = useState<string>('');
  const [nestConnectLoading, setNestConnectLoading] = useState(false);
  const [nestConnectionError, setNestConnectionError] = useState('');
  const [nestConnected, setNestConnected] = useState(false);
  const handleConnectNestPension = useCallback(async () => {
    const values = form.getFieldsValue(true);
    if (
      !values.nestUsername ||
      !values.nestPassword ||
      !values.nestEmployerId
    ) {
      setNestConnectionError(
        'Please fill in all required credentials (Username, Password, and NEST Employer ID)',
      );
      return;
    }
    setNestConnectionError('');
    setNestConnectLoading(true);
    try {
      const companyDraft = await createCompanyDraft();
      const companyId = companyDraft.id;
      const res = await connectNestPensionDraft(companyId, {
        username: values.nestUsername,
        password: values.nestPassword,
        employerRef: values.nestEmployerId,
        delegateId: values.nestDelegateId,
      });
      if (res.status === 'ERROR') {
        showMessage({ type: 'error', content: res.errorMessage });
        setNestConnectionError(res.errorMessage);
        setNestConnected(false);
      } else if (res.status === 'CONNECTED') {
        showMessage({
          type: 'success',
          content: 'Successfully connected to Pension provider!',
        });
        setNestConnected(true);
      } else {
        showMessage({ type: 'info', content: 'Connection in progress...' });
        startPolling(companyId);
      }
    } catch (error: any) {
      setNestConnectionError(
        error?.response?.data?.message || 'An error occurred',
      );
      setNestConnected(false);
    } finally {
      setNestConnectLoading(false);
    }
  }, []);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onProviderChange = () => {
    form.setFieldsValue({
      companyId: undefined,
      pensionSchemeId: undefined,
      pensionSchemeNumber: undefined,
      pensionCalculationType: undefined,
      pensionMethod: undefined,
      erPensionPercentage: undefined,
      eePensionPercentage: undefined,
      employerCode: undefined,
      payrollCode: undefined,
      schemeCodeWeekly: undefined,
      schemeCode2Weekly: undefined,
      schemeCode4Weekly: undefined,
      schemeCodeMonthly: undefined,
      isSalarySacrifice: false,
      jarvisGroupOrganizationApiKey: undefined,
      jarvisOrganizationID: undefined,
      jarvisPensionScheme: undefined,
      nestUsername: undefined,
      nestPassword: undefined,
      nestEmployerId: undefined,
      nestDelegateId: undefined,
    } as Partial<PensionContinueValues>);
  };

  // NX-5770: the statutory 3%/5% is a fallback, not an override — whatever is
  // already there (a resumed draft, or the scheme data SMART returns on
  // connect) must survive. NEST is excluded too: its inputs are never
  // rendered, so seeding them only persisted a rate nobody entered.
  useEffect(() => {
    if (!isSmartPension && !isNowPension) return;
    const isBlank = (field: keyof PensionContinueValues) => {
      const value = form.getFieldValue(field);
      return value === undefined || value === null || value === '';
    };
    const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
    const next: Partial<PensionContinueValues> = {};
    if (isBlank('erPensionPercentage')) next.erPensionPercentage = erMin;
    if (isBlank('eePensionPercentage'))
      next.eePensionPercentage = totalMin - erMin;
    if (Object.keys(next).length) form.setFieldsValue(next);
  }, [isSmartPension, isNowPension]);

  return (
    <div className="PensionContinue">
      {/* Step Header */}
      <div className="space-y-[6px]">
        <div className="bg-primary-50 border border-primary-300 rounded-full px-[8px] py-[2px] flex items-center gap-x-[8px] w-fit">
          <div className="w-[8px] h-[8px] rounded-full bg-primary-600" />
          <div className="text-[14px] text-primary-600">
            {`${messages['common.step']} ${stepNumber} (${messages['common.continue']})`}
          </div>
        </div>
        <div className="text-[22px]">
          {messages['company.pension.continue.useExistingProvider']}
        </div>
        <div className="text-[14px] text-neutral-500">
          {messages['company.pension.continue.description']}
        </div>
      </div>
      <div className="h-[8px] w-full bg-neutral-100 rounded-full mt-[24px]" />

      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          initialValues={{
            isSalarySacrifice: false,
            pensionCalculationType: { label: 'Net Pay Arrangement', value: 'NET_PAY_ARRANGEMENT' },
            pensionMethod: { label: 'Qualifying Earnings', value: 'QUALIFYING_EARNINGS' },
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          {/* Pension Provider + Provider-specific right field */}
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name="pensionProviderId"
                label={messages['common.pensionProvider']}
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                ]}
              >
                <AppSelect
                  labelInValue
                  options={providerOptions}
                  placeholder={messages['common.pensionProvider']}
                  onChange={onProviderChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {isSmartPension && (
                <Form.Item
                  name="companyId"
                  label={messages['common.companyId']}
                >
                  <AppInput
                    placeholder={messages['common.companyId']}
                    disabled
                  />
                </Form.Item>
              )}
              {isNowPension && (
                <Form.Item
                  name="employerCode"
                  label={messages['common.employerCode']}
                >
                  <AppInput placeholder={`e.g. 12345`} />
                </Form.Item>
              )}
              {isNestPension && (
                <Form.Item
                  name="aePostponementDays"
                  label={messages['common.aePostponementDays']}
                >
                  <AppSelect
                    labelInValue
                    options={defaultAEPostponementDaysOptions}
                    placeholder={messages['common.aePostponementDays']}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {/* Smart/NOW: Calculation Method + right field */}
          {isSmartOrNow && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="pensionCalculationType"
                  label={messages['common.calculationMethod']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    labelInValue
                    options={defaultPensionCalCulationMethodOptions}
                    placeholder={messages['common.calculationMethod']}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {isSmartPension && (
                  <Form.Item
                    name="pensionSchemeId"
                    label={messages['common.pensionSchemeId']}
                  >
                    <AppInput
                      placeholder={messages['common.pensionSchemeId']}
                      disabled
                    />
                  </Form.Item>
                )}
                {isNowPension && (
                  <Form.Item
                    name="payrollCode"
                    label={messages['common.payrollCode']}
                  >
                    <AppInput placeholder={`e.g. 12345`} />
                  </Form.Item>
                )}
              </Col>
            </Row>
          )}

          {/* Smart/NOW: Pension Method + right field */}
          {isSmartOrNow && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="pensionMethod"
                  label={messages['common.pensionMethod']}
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    labelInValue
                    options={defaultPensionMethodOptions}
                    placeholder={messages['common.pensionMethod']}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {isSmartPension && (
                  <Form.Item
                    name="pensionSchemeNumber"
                    label={messages['common.pensionSchemeNumber']}
                  >
                    <AppInput
                      placeholder={messages['common.pensionSchemeNumber']}
                      disabled
                    />
                  </Form.Item>
                )}
                {isNowPension && (
                  <Form.Item
                    name="schemeCodeWeekly"
                    label={messages['common.schemeCodeWeekly']}
                  >
                    <AppInput
                      placeholder={messages['common.schemeCodeWeekly']}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
          )}

          {/* NOW: Salary Sacrifice + Scheme Code 2 Weekly */}
          {isNowPension && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="isSalarySacrifice"
                  label={messages['common.salarySacrifice']}
                  valuePropName="checked"
                >
                  <AppSwitch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="schemeCode2Weekly"
                  label={messages['common.schemeCode2Weekly']}
                >
                  <AppInput
                    placeholder={messages['common.schemeCode2Weekly']}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Smart: AE Postponement Days (full width) */}
          {isSmartPension && (
            <Row gutter={[16, 0]}>
              <Col span={24}>
                <Form.Item
                  name="aePostponementDays"
                  label={messages['common.aePostponementDays']}
                >
                  <AppSelect
                    labelInValue
                    disabled
                    options={defaultAEPostponementDaysOptions}
                    placeholder={messages['common.aePostponementDays']}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* NOW: AE Postponement Days + Scheme Code 4 Weekly */}
          {isNowPension && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="aePostponementDays"
                  label={messages['common.aePostponementDays']}
                >
                  <AppSelect
                    labelInValue
                    options={defaultAEPostponementDaysOptions}
                    placeholder={messages['common.aePostponementDays']}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="schemeCode4Weekly"
                  label={messages['common.schemeCode4Weekly']}
                >
                  <AppInput
                    placeholder={messages['common.schemeCode4Weekly']}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* NOW: Scheme Code Monthly (full width left) */}
          {isNowPension && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="schemeCodeMonthly"
                  label={messages['common.schemeCodeMonthly']}
                >
                  <AppInput
                    placeholder={messages['common.schemeCodeMonthly']}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* NX-4803: Jarvis Pension integration settings hidden until complete
          {isJarvisPension && (
            <>
              <Row gutter={[16, 0]}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.groupOrganizationApiKey']}
                    name="jarvisGroupOrganizationApiKey"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.organizationId']}
                    name="jarvisOrganizationID"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.pensionScheme']}
                    name="jarvisPensionScheme"
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          */}

          {/* NX-5231: self-certification set — same Advanced expander as the
              company pension tab, collapsed by default and nothing
              pre-selected. Picking a set moves the validation floors below and
              fills the rates with that set's statutory minimum split. */}
          {isSmartOrNow && (
            <>
              <div className="flex items-center justify-between gap-[12px] mb-[12px]">
                <div className="text-[14px] font-bold">
                  {messages['common.pensionPercentages']}
                </div>
                <AppButton
                  type="primary"
                  name="Advanced"
                  onClick={() => setSelfCertExpanded((prev) => !prev)}
                  rightIcon={
                    selfCertExpanded ? <UpOutlined /> : <DownOutlined />
                  }
                />
              </div>
              <Form.Item name="pensionSelfCertification" hidden>
                <AppInput />
              </Form.Item>
              {selfCertExpanded && (
                <div className="mb-[16px] rounded-[8px] border border-solid border-[#EAECF0] p-[20px]">
                  <SelfCertificationPanel
                    value={selfCertValue}
                    providerName={selectedProvider?.label}
                    disabled={isSmartPension}
                    onChange={(val) => {
                      form.setFieldValue('pensionSelfCertification', val);
                      setSelfCertValue(val);
                      // NX-5231 feedback: the set is the rate choice, so
                      // picking one always writes that set's statutory minimum
                      // split into the rate fields — switching sets updates
                      // them rather than leaving the previous set's numbers.
                      const { totalMin, erMin } = getPensionSelfCertMins(val);
                      if (!isSmartPension) {
                        form.setFieldValue('erPensionPercentage', erMin);
                        form.setFieldValue(
                          'eePensionPercentage',
                          totalMin - erMin,
                        );
                      }
                      form
                        .validateFields([
                          'erPensionPercentage',
                          'eePensionPercentage',
                        ])
                        .catch(() => {});
                    }}
                    // Undo an accidental pick: back to uncertified, where the
                    // pre-NX-5231 floors apply, and the rates back to that
                    // default 3/5 split — the mirror of picking a set.
                    onClear={() => {
                      form.setFieldValue('pensionSelfCertification', '');
                      setSelfCertValue('');
                      const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
                      if (!isSmartPension) {
                        form.setFieldValue('erPensionPercentage', erMin);
                        form.setFieldValue(
                          'eePensionPercentage',
                          totalMin - erMin,
                        );
                      }
                      form
                        .validateFields([
                          'erPensionPercentage',
                          'eePensionPercentage',
                        ])
                        .catch(() => {});
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Smart/NOW: Employer + Employee percentages */}
          {isSmartOrNow && (
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <Form.Item
                  name="erPensionPercentage"
                  label={`${messages['common.employer']} %`}
                  rules={[
                    {
                      validator: (_, value) => {
                        const { totalMin, erMin } = getPensionSelfCertMins(
                          form.getFieldValue('pensionSelfCertification'),
                        );
                        const er = parseFloat(value);
                        if (value && !isNaN(er) && er < erMin) {
                          return Promise.reject(
                            `Employer contribution must be at least ${erMin}%`,
                          );
                        }
                        if (value && !isNaN(er) && (er < 0 || er > 100)) {
                          return Promise.reject(
                            'Please enter a value between 0% and 100%',
                          );
                        }
                        const eeValue =
                          form.getFieldValue('eePensionPercentage');
                        const ee = parseFloat(eeValue);
                        if ((er || 0) + (ee || 0) === 0) {
                          return Promise.reject(
                            'Total employer and employee pension contributions cannot be zero.',
                          );
                        }
                        if (!isNaN(er) && !isNaN(ee) && er + ee < totalMin) {
                          return Promise.reject(
                            `Total employer & employee contribution must be at least ${totalMin}%`,
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  dependencies={['eePensionPercentage']}
                >
                  <AppRateInput
                    placeholder="3"
                    disabled={isSmartPension}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="eePensionPercentage"
                  label={`${messages['common.employee']} %`}
                  rules={[
                    {
                      validator: (_, value) => {
                        const { totalMin } = getPensionSelfCertMins(
                          form.getFieldValue('pensionSelfCertification'),
                        );
                        const ee = parseFloat(value);
                        if (value && !isNaN(ee) && (ee < 0 || ee > 100)) {
                          return Promise.reject(
                            'Please enter a value between 0% and 100%',
                          );
                        }
                        const erValue =
                          form.getFieldValue('erPensionPercentage');
                        const er = parseFloat(erValue);
                        if ((er || 0) + (ee || 0) === 0) {
                          return Promise.reject(
                            'Total employer and employee pension contributions cannot be zero.',
                          );
                        }
                        if (!isNaN(er) && !isNaN(ee) && er + ee < totalMin) {
                          return Promise.reject(
                            `Total employer & employee contribution must be at least ${totalMin}%`,
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  dependencies={['erPensionPercentage']}
                >
                  <AppRateInput
                    placeholder="5"
                    disabled={isSmartPension}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Smart Pension: Connect Card */}
          {isSmartPension && (
            <>
              <div className="h-[8px] w-full bg-neutral-100 rounded-full mb-[24px]" />
              <Row>
                <Col sm={24} md={12}>
                  <div className="mt-[8px] w-[466px]">
                    <AppCard
                      className="p-[24px]"
                      style={{ backgroundColor: '#F3F4F8' }}
                    >
                      <div className="text-[18px] font-semibold">
                        {messages['common.connectToSmartPension']}
                      </div>
                      {pensionStatus?.smartPension.status === 'AUTHORIZED' ? (
                        <div className="flex items-center gap-x-[8px] text-success-600 text-[14px] mb-[12px]">
                          <div className="w-[8px] h-[8px] rounded-full bg-success-500" />
                          Connected
                        </div>
                      ) : (
                        <div className="text-[14px] text-neutral-500 mb-[12px]">
                          {isPollingPension
                            ? 'Waiting for Smart Pension authorization...'
                            : messages['common.waitingConnection']}
                        </div>
                      )}
                      {pensionPollingError && (
                        <div className="text-red-500 text-[14px] mb-[8px]">
                          {pensionPollingError}
                        </div>
                      )}
                      <AppButton
                        name={
                          pensionStatus?.smartPension.status === 'AUTHORIZED'
                            ? messages['common.reconnect']
                            : messages['common.connect']
                        }
                        variant="solid"
                        loading={isConnectingSmartPension || isPollingPension}
                        onClick={handleConnectSmartPension}
                      />
                    </AppCard>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* Nest Pension: Credentials Card */}
          {isNestPension && (
            <Row gutter={[16, 0]}>
              <Col sm={24} md={12}>
                <div className="mt-[8px]">
                  <AppCard
                    className="p-[24px]"
                    style={{ backgroundColor: '#F3F4F8' }}
                  >
                    <div className="text-[18px] font-semibold">
                      {messages['common.nestPensionCredentials']}
                    </div>
                    {nestConnected ? (
                      <div className="flex items-center gap-x-[8px] text-success-600 text-[14px] mb-[16px]">
                        <div className="w-[8px] h-[8px] rounded-full bg-success-500" />
                        Connected
                      </div>
                    ) : (
                      <div className="text-[14px] text-neutral-500 mb-[16px]">
                        {messages['common.waitingConnection']}
                      </div>
                    )}

                    <Form.Item
                      name="nestUsername"
                      label={messages['common.username']}
                    >
                      {/* AppInput capitalises the first letter by default, which corrupts a
                          lower-case Nest username and fails authentication. Credentials
                          are verbatim values — never transform them. */}
                      <AppInput
                        allowCapitalize={false}
                        placeholder={messages['common.username']}
                      />
                    </Form.Item>

                    <Form.Item
                      name="nestPassword"
                      label={messages['common.password']}
                    >
                      <AppInputPassword
                        placeholder={messages['common.password']}
                      />
                    </Form.Item>

                    <Form.Item
                      name="nestEmployerId"
                      label={messages['common.nestEmployerID']}
                    >
                      {/* Nest-issued identifier — pass through verbatim. */}
                      <AppInput
                        allowCapitalize={false}
                        placeholder={messages['common.nestEmployerID']}
                      />
                    </Form.Item>

                    <Form.Item
                      name="nestDelegateId"
                      label={messages['common.nestDelegateID']}
                    >
                      {/* Nest-issued identifier — pass through verbatim. */}
                      <AppInput
                        allowCapitalize={false}
                        placeholder={messages['common.nestDelegateID']}
                      />
                    </Form.Item>

                    {nestConnectionError && (
                      <div className="text-red-500 text-[14px] mb-[8px]">
                        {nestConnectionError}
                      </div>
                    )}
                    <AppButton
                      name={
                        nestConnected
                          ? messages['common.reconnect']
                          : messages['common.connect']
                      }
                      variant="solid"
                      loading={nestConnectLoading}
                      onClick={handleConnectNestPension}
                    />
                  </AppCard>
                </div>
              </Col>
            </Row>
          )}
        </AppFormContainer>
      </div>
    </div>
  );
};

export default PensionContinue;
